import { useEffect, useRef } from 'react';
import { ActionIcon, Badge, Box, Group, Image, Loader, Text, UnstyledButton } from '@mantine/core';
import type { KeyboardEvent, MouseEvent } from 'react';
import { Icon3dCubeSphere, IconTrash, IconAlertCircle } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import type { ProductDraft } from '@/hooks/useProductBatchCreate';
import { draftPreviewUri, isDraftDetailsComplete } from '@/hooks/useProductBatchCreate';
import { colorWithAlpha } from '@/utils/colorWithAlpha';
import classes from './ProductBatchStepper.module.css';

type ProductBatchStepperProps = {
  drafts: ProductDraft[];
  activeDraftId: string | null;
  accentColor: string;
  disabled?: boolean;
  onSelect: (localId: string) => void;
  onRemove: (localId: string) => void;
};

function draftStatusKey(draft: ProductDraft, selected: boolean): string {
  if (selected) return 'editProduct.batch.nowEditing';
  if (draft.status === 'converting' || draft.status === 'queued') {
    return 'editProduct.batch.statusConverting';
  }
  if (draft.status === 'error') {
    // Create API failure still has a model; conversion failure does not.
    return draft.modelUrl
      ? 'editProduct.batch.statusCreateFailed'
      : 'editProduct.batch.statusError';
  }
  if (isDraftDetailsComplete(draft)) return 'editProduct.batch.statusReady';
  return 'editProduct.batch.statusMissing';
}

export function ProductBatchStepper({
  drafts,
  activeDraftId,
  accentColor,
  disabled = false,
  onSelect,
  onRemove,
}: ProductBatchStepperProps) {
  const { t } = useTranslation();
  const activeIndex = Math.max(
    0,
    drafts.findIndex((draft) => draft.localId === activeDraftId)
  );
  const selectedRef = useRef<HTMLDivElement | null>(null);
  const activeDraft = drafts[activeIndex];

  useEffect(() => {
    selectedRef.current?.scrollIntoView({
      behavior: 'smooth',
      inline: 'center',
      block: 'nearest',
    });
  }, [activeDraftId]);

  return (
    <Box className={classes.wrap}>
      <Group justify="space-between" align="flex-start" wrap="nowrap" gap="sm">
        <div>
          <Text fw={700}>{t('editProduct.batch.queueTitle')}</Text>
          <Text size="xs" c="dimmed">
            {t('editProduct.batch.tapToSwitch')}
          </Text>
        </div>
        <Badge variant="light" color={accentColor} size="lg">
          {t('editProduct.batch.stepProgress', {
            current: drafts.length ? activeIndex + 1 : 0,
            total: drafts.length,
          })}
        </Badge>
      </Group>

      {activeDraft ? (
        <Box
          className={classes.currentBanner}
          style={{
            borderColor: accentColor,
            background: colorWithAlpha(accentColor, 0.1),
          }}
        >
          <Text size="xs" fw={700} tt="uppercase" style={{ color: accentColor }}>
            {t('editProduct.batch.nowEditing')}
          </Text>
          <Text fw={700} lineClamp={1}>
            {activeDraft.productName ||
              t('editProduct.batch.unnamedStep', { n: activeIndex + 1 })}
          </Text>
          <Text size="xs" c="dimmed" lineClamp={1}>
            {activeDraft.file.name}
          </Text>
        </Box>
      ) : null}

      <Box className={classes.rail} role="list">
        {drafts.map((draft, index) => {
          const selected = draft.localId === activeDraftId;
          const busy = draft.status === 'creating' || draft.status === 'converting';
          const preview = draftPreviewUri(draft);
          const statusKey = draftStatusKey(draft, selected);

          return (
            <Box key={draft.localId} className={classes.step} role="listitem">
              {index > 0 ? (
                <Box
                  className={classes.connector}
                  style={{
                    background: index <= activeIndex ? accentColor : undefined,
                    opacity: index <= activeIndex ? 0.9 : 0.45,
                  }}
                />
              ) : null}

              <UnstyledButton
                component="div"
                ref={selected ? selectedRef : undefined}
                onClick={() => onSelect(draft.localId)}
                onKeyDown={(event: KeyboardEvent<HTMLDivElement>) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    onSelect(draft.localId);
                  }
                }}
                disabled={disabled}
                tabIndex={disabled ? -1 : 0}
                aria-current={selected ? 'step' : undefined}
                className={`${classes.card}${selected ? ` ${classes.cardSelected}` : ''}`}
                style={{
                  borderColor: selected ? accentColor : undefined,
                  background: selected ? colorWithAlpha(accentColor, 0.12) : undefined,
                  boxShadow: selected
                    ? `0 10px 24px ${colorWithAlpha(accentColor, 0.22)}`
                    : undefined,
                  cursor: disabled ? 'default' : 'pointer',
                }}
              >
                <Group justify="space-between" wrap="nowrap" gap={6}>
                  <Box
                    className={classes.index}
                    style={{
                      background: selected ? accentColor : colorWithAlpha(accentColor, 0.16),
                      color: selected ? '#fff' : accentColor,
                    }}
                  >
                    {index + 1}
                  </Box>
                  <ActionIcon
                    variant="subtle"
                    color="red"
                    size="sm"
                    disabled={disabled || busy}
                    onClick={(event: MouseEvent<HTMLButtonElement>) => {
                      event.stopPropagation();
                      onRemove(draft.localId);
                    }}
                    aria-label={t('editProduct.batch.remove')}
                  >
                    <IconTrash size={14} />
                  </ActionIcon>
                </Group>

                <Box className={`${classes.thumb}${selected ? ` ${classes.thumbSelected}` : ''}`}>
                  {preview ? (
                    <Image src={preview} alt="" w="100%" h="100%" fit="contain" />
                  ) : busy || draft.status === 'queued' ? (
                    <Loader size="sm" color={accentColor} />
                  ) : draft.status === 'error' ? (
                    <IconAlertCircle size={22} color="var(--mantine-color-red-6)" />
                  ) : (
                    <Icon3dCubeSphere size={22} color={accentColor} />
                  )}
                </Box>

                <Text size="sm" fw={700} lineClamp={1}>
                  {draft.productName || t('editProduct.batch.unnamedStep', { n: index + 1 })}
                </Text>
                <Text size="xs" c="dimmed" lineClamp={1}>
                  {draft.file.name}
                </Text>
                <Text
                  size="xs"
                  fw={selected ? 700 : 500}
                  mt={4}
                  style={{ color: selected ? accentColor : undefined }}
                  c={selected ? undefined : 'dimmed'}
                >
                  {t(statusKey)}
                  {draft.annotations.length > 0
                    ? ` · ${t('editProduct.batch.annotationCount', { count: draft.annotations.length })}`
                    : ''}
                </Text>
              </UnstyledButton>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
